/**
 * DAVMAN AI INTEGRATION
 * Este archivo es parte de la integración personalizada de IA para Davman Persa.
 * NO es parte del código original de Chatwoot.
 *
 * Cliente API para comunicarse con el microservicio Davman AI
 */

import axios from 'axios';

/**
 * URL base del microservicio Davman AI
 * En producción, esto debería venir de una variable de entorno
 */
const DAVMAN_AI_URL = window.davmanAiConfig?.url || 'http://localhost:3001';

/**
 * Cliente HTTP configurado para Davman AI
 */
const client = axios.create({
  baseURL: DAVMAN_AI_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Obtiene la sugerencia de IA para una conversación
 *
 * @param {number} conversationId - ID de la conversación
 * @returns {Promise<{hasSuggestion: boolean, suggestion: object|null}>}
 */
export const getSuggestion = async (conversationId) => {
  try {
    const response = await client.get(`/api/suggestions/${conversationId}`);
    return response.data;
  } catch (error) {
    console.error('[Davman AI] Error fetching suggestion:', error);
    throw error;
  }
};

/**
 * Marca la sugerencia como usada
 *
 * @param {number} conversationId - ID de la conversación
 * @returns {Promise<{success: boolean}>}
 */
export const useSuggestion = async (conversationId) => {
  try {
    const response = await client.post(`/api/suggestions/${conversationId}/use`);
    return response.data;
  } catch (error) {
    console.error('[Davman AI] Error marking suggestion as used:', error);
    throw error;
  }
};

/**
 * Descarta la sugerencia
 *
 * @param {number} conversationId - ID de la conversación
 * @returns {Promise<{success: boolean}>}
 */
export const dismissSuggestion = async (conversationId) => {
  try {
    const response = await client.post(`/api/suggestions/${conversationId}/dismiss`);
    return response.data;
  } catch (error) {
    console.error('[Davman AI] Error dismissing suggestion:', error);
    throw error;
  }
};

/**
 * Verifica si el servicio Davman AI está disponible
 *
 * @returns {Promise<boolean>}
 */
export const checkHealth = async () => {
  try {
    const response = await client.get('/health');
    return response.data.status === 'healthy';
  } catch {
    return false;
  }
};

// =============================================================================
// ADMIN API
// =============================================================================

/**
 * Obtiene el resumen del dashboard de admin
 */
export const getDashboard = async () => {
  const response = await client.get('/admin/dashboard');
  return response.data;
};

/**
 * Obtiene la configuración de personalidad
 */
export const getPersonality = async () => {
  const response = await client.get('/admin/personality');
  return response.data;
};

/**
 * Actualiza la configuración de personalidad
 */
export const updatePersonality = async (data) => {
  const response = await client.patch('/admin/personality', data);
  return response.data;
};

/**
 * Obtiene todas las FAQs
 */
export const getFaqs = async () => {
  const response = await client.get('/admin/faqs');
  return response.data;
};

/**
 * Obtiene una FAQ específica
 */
export const getFaq = async (id) => {
  const response = await client.get(`/admin/faqs/${id}`);
  return response.data;
};

/**
 * Crea una nueva FAQ
 */
export const createFaq = async (data) => {
  const response = await client.post('/admin/faqs', data);
  return response.data;
};

/**
 * Actualiza una FAQ
 */
export const updateFaq = async (id, data) => {
  const response = await client.patch(`/admin/faqs/${id}`, data);
  return response.data;
};

/**
 * Elimina una FAQ
 */
export const deleteFaq = async (id) => {
  const response = await client.delete(`/admin/faqs/${id}`);
  return response.data;
};

/**
 * Obtiene la configuración general
 */
export const getSettings = async () => {
  const response = await client.get('/admin/settings');
  return response.data;
};

/**
 * Actualiza la configuración general
 */
export const updateSettings = async (data) => {
  const response = await client.patch('/admin/settings', data);
  return response.data;
};

/**
 * Obtiene las promociones
 */
export const getPromotions = async () => {
  const response = await client.get('/admin/promotions');
  return response.data;
};

/**
 * Actualiza las promociones
 */
export const updatePromotions = async (data) => {
  const response = await client.patch('/admin/promotions', data);
  return response.data;
};

/**
 * Obtiene las estadísticas
 */
export const getStats = async () => {
  const response = await client.get('/admin/stats');
  return response.data;
};

/**
 * Reinicia las estadísticas
 */
export const resetStats = async () => {
  const response = await client.post('/admin/stats/reset');
  return response.data;
};

export default {
  // Suggestions
  getSuggestion,
  useSuggestion,
  dismissSuggestion,
  checkHealth,
  // Admin
  getDashboard,
  getPersonality,
  updatePersonality,
  getFaqs,
  getFaq,
  createFaq,
  updateFaq,
  deleteFaq,
  getSettings,
  updateSettings,
  getPromotions,
  updatePromotions,
  getStats,
  resetStats,
};
